import { useEffect, useLayoutEffect, type RefObject } from 'react';
import { collectScrollRepositionTargets } from '../utils/popupPosition';

const MAX_LAYOUT_ATTEMPTS = 80;

/**
 * Mide el panel al abrir y recalcula en scroll/resize cuando el popup no es `absolute`.
 */
export function usePopupPositioning(params: {
  isOpen: boolean;
  isPositioned: boolean;
  absolute: boolean;
  contentRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLDivElement | null>;
  externalTriggerRef: RefObject<HTMLElement | null> | undefined;
  calculatePosition: () => void;
}): void {
  const {
    isOpen,
    isPositioned,
    absolute,
    contentRef,
    triggerRef,
    externalTriggerRef,
    calculatePosition,
  } = params;

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const attemptPosition = () => {
      if (cancelled || attempts++ > MAX_LAYOUT_ATTEMPTS) {
        return;
      }
      const popupElement = contentRef.current;
      if (!popupElement) {
        requestAnimationFrame(attemptPosition);
        return;
      }
      const rect = popupElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        calculatePosition();
        requestAnimationFrame(() => {
          if (!cancelled) {
            calculatePosition();
          }
        });
      } else {
        requestAnimationFrame(attemptPosition);
      }
    };

    attemptPosition();
    return () => {
      cancelled = true;
    };
  }, [isOpen, calculatePosition, contentRef]);

  useEffect(() => {
    if (!isOpen || !isPositioned || absolute) {
      return;
    }

    let ticking = false;
    const updatePosition = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          calculatePosition();
          ticking = false;
        });
      }
    };

    const anchorEl = externalTriggerRef?.current ?? triggerRef.current;
    const scrollTargets = collectScrollRepositionTargets(anchorEl);
    scrollTargets.forEach((target) => {
      target.addEventListener('scroll', updatePosition, { passive: true });
    });
    window.addEventListener('resize', updatePosition, { passive: true });

    return () => {
      scrollTargets.forEach((target) => {
        target.removeEventListener('scroll', updatePosition);
      });
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, isPositioned, absolute, calculatePosition, externalTriggerRef, triggerRef]);
}
